import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { ReactComponent as Dolar } from '../../../../assets/svg/currency-circle.svg'
import { ReactComponent as PixLogo } from '../../../../assets/svg/pixLogo.svg'

const getRefundText = (type, product) => {
  const refundTypeMap = {
    wirecard: product === 'Pix' ? 'Reembolso por Pix' : 'Reembolso por estorno no cartão de crédito',
    starkbank: 'Reembolso por depósito em conta',
    'ordem de pagamento': 'Reembolso por ordem de pagamento',
    pix: 'Reembolso por Pix'
  }

  return refundTypeMap[type] || 'Reembolso'
}

export const useCardRequestRefundFlow = ({ cancellationData, cridInfo, refundFlowStatus, onStatusChange }) => {
  const [pixConfirmationData, setPixConfirmationData] = useState(null)
  const [persistHide, setPersistHide] = useState(false)

  const cancellationId = cancellationData?.cancellationRequest?.id
  const productType = cancellationData?.refundSummary?.ProductType || cridInfo?.ProductType || cridInfo?.productType
  const refundAmount = cancellationData?.refundSummary?.refundAmount ?? cancellationData?.cancellationRequest?.refundValue
  const refundType = cancellationData?.cancellationRequest?.refundType
  const cancellationSubType = cancellationData?.cancellationRequest?.cancellationSubType

  const paymentInfoFromApi =
    cancellationData?.cancellationRequest?.paymentInformation ?? cancellationData?.refundSummary?.paymentInformation

  const persistHideKey = cancellationId ? `pix-hide:${cancellationId}` : null
  const persistPixKey = cancellationId ? `pix-info:${cancellationId}` : null

  useEffect(() => {
    if (!persistHideKey) return
    const flag = sessionStorage.getItem(persistHideKey) === '1'
    setPersistHide(flag)
  }, [persistHideKey])

  const markPersistHidden = useCallback(() => {
    if (!persistHideKey) return
    try {
      sessionStorage.setItem(persistHideKey, '1')
      setPersistHide(true)
    } catch {}
  }, [persistHideKey])

  useEffect(() => {
    if (!persistPixKey) return
    try {
      const cached = sessionStorage.getItem(persistPixKey)
      if (cached && !pixConfirmationData) {
        setPixConfirmationData(JSON.parse(cached))
      }
    } catch {}
  }, [persistPixKey, pixConfirmationData])

  const pixDisplay = pixConfirmationData?.success
    ? pixConfirmationData
    : paymentInfoFromApi
    ? {
        pixKey: paymentInfoFromApi.pixKey,
        pixType: paymentInfoFromApi.pixType,
        recipient: paymentInfoFromApi.recipient,
        CPF: paymentInfoFromApi.CPF,
        success: true
      }
    : pixConfirmationData

  const showPixError = refundFlowStatus === 'pixError' && !pixConfirmationData?.success
  const hasPixInfo = !!pixDisplay?.success
  const inPixUpdateFlow = showPixError || hasPixInfo || persistHide
  const isInWarrantyPeriod = cancellationData?.cancellationRequest?.warrantyPeriod === 'in_warranty_period'
  const isInAnalysisStatus = cancellationData?.cancellationRequest?.status === 'analysis'

  const canRevokeCancellation = isInWarrantyPeriod && isInAnalysisStatus

  const effectiveStatus = (showPixError ? 'pixError' : hasPixInfo ? 'inProgress' : refundFlowStatus) || null

  const refundInfo = useMemo(() => {
    const isPixRefund = refundType === 'pix' || (refundType === 'wirecard' && productType === 'Pix')

    return {
      icon: isPixRefund ? <PixLogo /> : <Dolar />,
      text: getRefundText(refundType, productType)
    }
  }, [refundType, productType])

  useEffect(() => {
    if (!onStatusChange || !effectiveStatus) return
    onStatusChange(effectiveStatus)
  }, [onStatusChange, effectiveStatus])

  return {
    pixConfirmationData,
    setPixConfirmationData,
    markPersistHidden,
    persistPixKey,
    pixDisplay,
    showPixError,
    inPixUpdateFlow,
    canRevokeCancellation,
    refundInfo,
    refundAmount,
    refundType,
    productType,
    cancellationId,
    cancellationSubType
  }
}
